import { useCallback, useEffect, useLayoutEffect, useReducer, useRef } from "react";

import { compileFilter } from "../store/local-query";
import {
  buildTree,
  collectNodes,
  filterTree,
  flattenTree,
  markHasChildren,
  parentKeysOf,
  sortTree,
  treeKey,
  treeKeySet,
} from "./tree";

const EMPTY_TREE = buildTree([], { keyExpr: "id", parentIdExpr: "parentId", rootValue: null });

function statusReducer(state, action) {
  switch (action.type) {
    case "start":
      return { ...state, phase: action.phase, error: null };
    case "done":
      return { phase: "idle", error: null, version: state.version + 1 };
    case "error":
      return { ...state, phase: "idle", error: action.error };
    case "touch":
      return { ...state, version: state.version + 1 };
    default:
      return state;
  }
}

export function useTreeData(params) {
  const {
    source,
    keyExpr,
    parentIdExpr,
    rootValue,
    filter,
    sort,
    filterMode,
    refreshToken,
    paged,
    pageSize,
    pageIndex,
  } = params;
  const [status, dispatch] = useReducer(statusReducer, {
    phase: "initial",
    error: null,
    version: 0,
  });
  const paramsRef = useRef(params);
  const itemsRef = useRef(null);
  const treeRef = useRef(EMPTY_TREE);
  const rowsRef = useRef([]);
  const expandedRef = useRef(null);
  const loadedRef = useRef(false);
  const lastFilterRef = useRef(null);
  const lastRefreshRef = useRef(refreshToken);
  const lastSourceRef = useRef(null);
  const abortRef = useRef(null);

  paramsRef.current = params;

  if (expandedRef.current === null) expandedRef.current = treeKeySet(params.initialExpandedKeys);

  const isExpanded = useCallback(key => expandedRef.current.has(treeKey(key)), []);

  const flatten = useCallback(() => {
    rowsRef.current = flattenTree(treeRef.current.root, isExpanded);
  }, [isExpanded]);

  const rebuild = useCallback(
    reload => {
      const { current } = paramsRef;
      const items =
        current.source.kind === "local" ? current.source.getItems() || [] : itemsRef.current || [];
      const tree = buildTree(items, {
        keyExpr: current.keyExpr,
        parentIdExpr: current.parentIdExpr,
        rootValue: current.rootValue,
      });

      sortTree(tree.root, current.sort, current.getGetter);
      tree.nodes = collectNodes(tree.root);

      const predicate = current.filter ? compileFilter(current.filter, current.getGetter) : null;

      filterTree(tree.nodes, predicate, current.filterMode);
      markHasChildren(tree.root);

      const filterKey = JSON.stringify(current.filter ?? null);
      const firstLoad = !loadedRef.current;
      const filterChanged = !firstLoad && lastFilterRef.current !== filterKey;
      let expansion = null;

      if (current.autoExpandAll && (firstLoad || (filterChanged && !predicate)))
        expansion = "expand";

      if (current.expandNodesOnFiltering !== false) {
        if (predicate && (filterChanged || firstLoad || reload)) expansion = "expand";
        else if (!predicate && filterChanged && !current.autoExpandAll) expansion = "collapse";
      }

      if (expansion === "expand") expandedRef.current = new Set(parentKeysOf(tree.nodes));
      else if (expansion === "collapse") expandedRef.current = new Set();

      loadedRef.current = true;
      lastFilterRef.current = filterKey;
      treeRef.current = tree;
      flatten();
    },
    [flatten]
  );

  const abort = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = null;
  }, []);

  const loadRemote = useCallback(
    phase => {
      abort();

      const controller = new AbortController();

      abortRef.current = controller;
      dispatch({ type: "start", phase });

      Promise.resolve()
        .then(() => paramsRef.current.source.load({}, { signal: controller.signal }))
        .then(result => {
          if (controller.signal.aborted) return;

          abortRef.current = null;
          itemsRef.current = result.data;
          rebuild(true);
          dispatch({ type: "done" });
        })
        .catch(error => {
          if (controller.signal.aborted || error?.name === "AbortError") return;

          abortRef.current = null;
          dispatch({ type: "error", error });
        });
    },
    [abort, rebuild]
  );

  const queryKey = JSON.stringify([
    filter ?? null,
    (sort || []).map(({ selector, desc }) => [
      typeof selector === "string" ? selector : "fn",
      !!desc,
    ]),
    filterMode ?? null,
    keyExpr ?? null,
    parentIdExpr ?? null,
    rootValue ?? null,
  ]);

  useLayoutEffect(() => {
    const refreshed = lastRefreshRef.current !== refreshToken;
    const sourceChanged = lastSourceRef.current !== source;

    lastRefreshRef.current = refreshToken;
    lastSourceRef.current = source;

    if (sourceChanged) loadedRef.current = false;

    if (source.kind !== "local") {
      if (refreshed || sourceChanged || !itemsRef.current) {
        loadRemote(itemsRef.current ? "reloading" : "initial");
        return;
      }

      if (abortRef.current) return;
    }

    rebuild(refreshed);
    dispatch({ type: "done" });
  }, [loadRemote, queryKey, rebuild, refreshToken, source]);

  useEffect(() => abort, [abort]);

  const setExpanded = useCallback(
    (keys, expanded) => {
      const next = new Set(expandedRef.current);
      let changed = false;

      keys.forEach(key => {
        const normalized = treeKey(key);

        if (expanded && !next.has(normalized)) {
          next.add(normalized);
          changed = true;
        } else if (!expanded && next.has(normalized)) {
          next.delete(normalized);
          changed = true;
        }
      });

      if (!changed) return false;

      expandedRef.current = next;
      flatten();
      dispatch({ type: "touch" });

      return true;
    },
    [flatten]
  );

  const setExpandedKeys = useCallback(
    keys => {
      expandedRef.current = treeKeySet(keys);
      flatten();
      dispatch({ type: "touch" });
    },
    [flatten]
  );

  const setAllExpanded = useCallback(
    expanded => setExpandedKeys(expanded ? parentKeysOf(treeRef.current.nodes) : []),
    [setExpandedKeys]
  );

  const getExpandedKeys = useCallback(() => Array.from(expandedRef.current), []);

  const getTree = useCallback(() => treeRef.current, []);

  const retry = useCallback(() => {
    if (paramsRef.current.source.kind === "local") return;

    loadRemote(itemsRef.current ? "reloading" : "initial");
  }, [loadRemote]);

  const allRows = rowsRef.current;
  const pageCount = paged ? Math.max(1, Math.ceil(allRows.length / pageSize)) : 1;
  const currentPage = paged ? Math.min(Math.max(pageIndex, 0), pageCount - 1) : 0;
  const rows = paged
    ? allRows.slice(currentPage * pageSize, (currentPage + 1) * pageSize)
    : allRows;

  return {
    rows,
    allRows,
    tree: treeRef.current,
    totalCount: allRows.length,
    pageCount,
    pageIndex: currentPage,
    phase: status.phase,
    error: status.error,
    version: status.version,
    retry,
    isExpanded,
    setExpanded,
    setExpandedKeys,
    setAllExpanded,
    getExpandedKeys,
    getTree,
  };
}
